/**
 * 权限工具函数
 * 用于检查用户权限、路由守卫、菜单显示、按钮控制
 */

/**
 * 获取当前用户的权限列表
 * @returns {string[]} 权限 code 数组
 */
export function getUserPermissions() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  return user.permissions || [];
}

/**
 * 检查用户是否有指定权限
 * @param {string|string[]} permissionCodes - 权限 code 或权限 code 数组
 * @param {boolean} requireAll - 是否要求拥有所有权限（默认 false，只要有一个即可）
 * @returns {boolean}
 */
export function hasPermission(permissionCodes, requireAll = false) {
  const userPermissions = getUserPermissions();

  if (!permissionCodes) {
    return true; // 没有权限要求，默认允许
  }

  const codes = Array.isArray(permissionCodes)
    ? permissionCodes
    : [permissionCodes];

  if (codes.length === 0) {
    return true;
  }

  if (requireAll) {
    // 要求拥有所有权限
    return codes.every((code) => userPermissions.includes(code));
  } else {
    // 只要有一个权限即可
    return codes.some((code) => userPermissions.includes(code));
  }
}

/**
 * 权限检查 HOC/组件包装器
 * 用于控制组件显示/隐藏
 */
export function withPermission(
  Component,
  requiredPermissions,
  fallback = null
) {
  return function PermissionWrapper(props) {
    if (hasPermission(requiredPermissions)) {
      return <Component {...props} />;
    }
    return fallback;
  };
}

/**
 * 路由权限映射
 * 根据路由路径返回需要的权限
 */
export const ROUTE_PERMISSIONS = {
  "/cases": ["case:list:all", "case:list:self"],
  "/cases/:id": ["case:view"],
  "/assign": ["return:list", "return:review"],
  "/letters": ["letter:review"],
  "/users": ["user:view", "user:manage"],
  "/roles": ["system:roles"],
  "/dictionaries": ["system:dicts"],
  "/reviewLevels": ["reviewLevel:view", "reviewLevel:manage"],
  "/teams": ["team:manage"],
  "/lawFirms": ["lawFirm:manage"],
};

/**
 * 检查路由权限
 * @param {string} pathname - 路由路径
 * @returns {boolean}
 */
export function checkRoutePermission(pathname) {
  // 个人中心相关路由不需要权限检查
  if (pathname.startsWith("/profile")) {
    return true;
  }

  // 精确匹配
  if (ROUTE_PERMISSIONS[pathname]) {
    return hasPermission(ROUTE_PERMISSIONS[pathname]);
  }

  // 动态路由匹配
  for (const [route, permissions] of Object.entries(ROUTE_PERMISSIONS)) {
    const routePattern = route.replace(/:[^/]+/g, "[^/]+");
    const regex = new RegExp(`^${routePattern}$`);
    if (regex.test(pathname)) {
      return hasPermission(permissions);
    }
  }

  // 默认允许访问（如果没有配置权限要求）
  return true;
}

/**
 * 菜单权限映射
 */
export const MENU_PERMISSIONS = {
  cases: ["case:list:all", "case:list:self"],
  assign: ["return:list", "return:review"],
  letters: ["letter:review"],
  users: ["user:view", "user:manage"],
  roles: ["system:roles"],
  dictionaries: ["system:dicts"],
  reviewLevels: ["reviewLevel:view", "reviewLevel:manage"],
  teams: ["team:manage"],
  lawFirms: ["lawFirm:manage"],
};

/**
 * 检查菜单项是否应该显示
 * @param {string} menuKey - 菜单 key
 * @returns {boolean}
 */
export function shouldShowMenu(menuKey) {
  const permissions = MENU_PERMISSIONS[menuKey];
  if (!permissions) {
    return true; // 没有配置权限要求，默认显示
  }
  return hasPermission(permissions);
}
